import { useState } from 'react'

import { fotoDeHerramienta } from '@/lib/supabase/deposito'
import type { Herramienta } from '@/lib/supabase/tipos-bd'
import { useHerramientasDisponibles } from '@/modules/programacion/useHerramientas'
import { IconoHerramienta } from '@/shared/ui/Iconos/Iconos'

import estilos from './HerramientasDeLaCotizacion.module.css'

/**
 * 0318 · HERRAMIENTAS REQUERIDAS PARA LA EJECUCIÓN DEL TRABAJO.
 *
 * El técnico que va a cotizar a la obra sabe de primera mano qué equipo se
 * necesita (escalera de 28 o 32 pasos, arnés, rotomartillo, etc.).
 *
 * Se marca aquí de forma opcional. No bloquea por disponibilidad actual de
 * bodega porque es una proyección para el día que se ejecute la orden.
 * Al pasar a programación, gerencia ya las ve precargadas en el modal.
 */

type Props = {
  eleccion: Record<string, number>
  onCambiar: (eleccion: Record<string, number>) => void
}

export function HerramientasDeLaCotizacion({ eleccion, onCambiar }: Props) {
  const { herramientas, cargando } = useHerramientasDisponibles()
  const [abierto, setAbierto] = useState(false)

  const cuantasElegidas = Object.values(eleccion).filter((c) => c > 0).length

  function alternar(h: Herramienta) {
    const siguiente = { ...eleccion }
    if (siguiente[h.id]) {
      delete siguiente[h.id]
    } else {
      siguiente[h.id] = 1
    }
    onCambiar(siguiente)
  }

  function cambiarCantidad(h: Herramienta, cuantas: number) {
    const tope = Math.max(1, h.cantidad_total || 1)
    onCambiar({
      ...eleccion,
      [h.id]: Math.max(1, Math.min(cuantas, tope)),
    })
  }

  if (cargando && herramientas.length === 0) return null

  return (
    <section className={estilos.zona}>
      <button
        type="button"
        className={estilos.cabecera}
        onClick={() => setAbierto((prev) => !prev)}
        aria-expanded={abierto}
      >
        <span className={estilos.cabeceraIzquierda}>
          <IconoHerramienta className={estilos.icono} />
          <span className={estilos.titulo}>Herramientas que se necesitan</span>
          <span
            className={`${estilos.cuenta} ${
              cuantasElegidas > 0 ? estilos.cuentaConDatos : ''
            }`}
          >
            {cuantasElegidas === 0
              ? 'Ninguna'
              : `${cuantasElegidas} ${
                  cuantasElegidas === 1 ? 'herramienta' : 'herramientas'
                }`}
          </span>
        </span>
        <span
          className={`${estilos.flecha} ${
            abierto ? estilos.flechaAbierta : ''
          }`}
        >
          ▼
        </span>
      </button>

      {abierto && (
        <div className={estilos.cuerpo}>
          <p className={estilos.subtitulo}>
            Marca las herramientas que harán falta en la obra. Al programar la orden,
            gerencia las tendrá listas sin tener que preguntarte.
          </p>

          <ul className={estilos.lista}>
            {herramientas.map((h) => {
              const marcada = (eleccion[h.id] ?? 0) > 0
              const tope = Math.max(1, h.cantidad_total || 1)

              return (
                <li key={h.id} className={estilos.fila}>
                  <label className={estilos.marca}>
                    <input
                      type="checkbox"
                      checked={marcada}
                      onChange={() => alternar(h)}
                    />
                    {fotoDeHerramienta(h.foto) && (
                      <img
                        className={estilos.foto}
                        src={fotoDeHerramienta(h.foto)!}
                        alt=""
                        loading="lazy"
                      />
                    )}
                    <span className={estilos.nombre}>{h.nombre}</span>
                    <span className={estilos.codigo}>{h.codigo}</span>
                  </label>

                  <div className={estilos.derecha}>
                    {marcada && tope > 1 && (
                      <span className={estilos.contador}>
                        <button
                          type="button"
                          onClick={() =>
                            cambiarCantidad(h, (eleccion[h.id] ?? 1) - 1)
                          }
                          disabled={(eleccion[h.id] ?? 1) <= 1}
                          aria-label={`Una menos de ${h.nombre}`}
                        >
                          −
                        </button>
                        <b>{eleccion[h.id] ?? 1}</b>
                        <button
                          type="button"
                          onClick={() =>
                            cambiarCantidad(h, (eleccion[h.id] ?? 1) + 1)
                          }
                          disabled={(eleccion[h.id] ?? 1) >= tope}
                          aria-label={`Una más de ${h.nombre}`}
                        >
                          +
                        </button>
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}
